/**
 * activeTenant.ts - which tenant THIS browser session is working in.
 *
 * An operator can belong to several tenants, and an agency operator will belong
 * to many. The dialer resolves each request's tenant from the X-Act-As-Tenant
 * header when one is sent, validated server-side against the caller's own
 * memberships, and otherwise from the operator's stored default_tenant.
 *
 * This app used to switch tenants by POSTing /api/switch-tenant, which REWROTE
 * that stored default_tenant: one field shared by every tab, every device and
 * every background job signed in as the same person. Switching in one tab
 * silently repointed all the others on their next request. apps/mas never
 * worked that way; it sends the header. This module brings operator into line.
 *
 * The model, the same as apps/mas's host session:
 *   - the active tenant lives IN MEMORY for this tab, and every request to the
 *     dialer carries it as X-Act-As-Tenant;
 *   - it is also saved per operator in localStorage, so a reload or a new tab
 *     starts where the operator left off, while a tab that is already open keeps
 *     its own choice until it reloads;
 *   - nothing here ever writes default_tenant.
 *
 * The header grants nothing. The dialer re-points a request only when the
 * operator is authorised for that tenant, and answers 403 before any data is
 * read otherwise.
 */

export const ACT_AS_TENANT_HEADER = 'X-Act-As-Tenant';

const STORAGE_PREFIX = 'operator.activeTenant.';

let active: string | null = null;

export function storageKey(uid: string): string {
  return `${STORAGE_PREFIX}${uid}`;
}

export function readPersistedActiveTenant(uid: string): string | null {
  try {
    const value = window.localStorage.getItem(storageKey(uid));
    return value && value.trim() ? value : null;
  } catch {
    return null; // storage blocked (private mode, policy): start on the default
  }
}

export function writePersistedActiveTenant(uid: string, tenantId: string | null): void {
  try {
    if (tenantId) window.localStorage.setItem(storageKey(uid), tenantId);
    else window.localStorage.removeItem(storageKey(uid));
  } catch {
    // storage blocked: the choice still applies to this tab, in memory
  }
}

/** Load this tab's active tenant for the signed-in operator (null when signed out). */
export function initActiveTenant(uid: string | null): string | null {
  active = uid ? readPersistedActiveTenant(uid) : null;
  return active;
}

/** The tenant this tab's requests are scoped to, or null for the operator's default. */
export function getActiveTenant(): string | null {
  return active;
}

/** Forget the saved choice, so this tab falls back to the operator's default. */
export function clearActiveTenant(uid: string): void {
  writePersistedActiveTenant(uid, null);
  active = null;
}

let reloadPage: () => void = () => window.location.reload();

/** Test seam: replace the page reload. Returns a function that restores it. */
export function setReloadForTests(fn: () => void): () => void {
  const previous = reloadPage;
  reloadPage = fn;
  return () => {
    reloadPage = previous;
  };
}

/**
 * Scope this browser session to `tenantId`, then reload into it.
 *
 * The reload is deliberate. This app keeps per-tenant data in component state
 * and in some twenty hand-rolled fetch modules, not only in a query cache, so a
 * reload is the one reset guaranteed to leave no tenant-A data on screen once
 * tenant B is chosen. What changed is that the choice no longer escapes this
 * browser: the old reload followed a write to default_tenant.
 */
export function switchActiveTenant(uid: string, tenantId: string): void {
  writePersistedActiveTenant(uid, tenantId);
  active = tenantId;
  reloadPage();
}

// The dialer origins this app talks to. VITE_API_URL is authoritative in every
// build; the two fallbacks are the literals the API modules default to when it
// is unset, listed so a local run behaves the same way.
const FALLBACK_DIALER_ORIGINS = [
  'https://sales-api-768596480524.us-central1.run.app',
  'http://localhost:8000',
];

export function dialerOrigins(): Set<string> {
  const origins = new Set<string>();
  for (const raw of [import.meta.env.VITE_API_URL, ...FALLBACK_DIALER_ORIGINS]) {
    if (!raw) continue;
    try {
      origins.add(new URL(raw).origin);
    } catch {
      // not a URL; ignore
    }
  }
  return origins;
}

const UNINSTALL_KEY = '__operatorActiveTenantFetchUninstall';

type FetchHost = { fetch: typeof fetch };

/**
 * Set `fetch` on `host`, including where it is a read-only but configurable
 * property, which is how some fetch instrumentation (and msw, under test)
 * installs itself. A plain assignment there throws in strict mode, and would
 * leave every request unscoped.
 */
function setFetch(host: FetchHost, fn: typeof fetch): void {
  try {
    host.fetch = fn;
    if (host.fetch === fn) return;
  } catch {
    // read-only data property: fall through to defineProperty
  }
  Object.defineProperty(host, 'fetch', {
    value: fn,
    writable: true,
    configurable: true,
    enumerable: true,
  });
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

/**
 * Make every request to the dialer carry this tab's active tenant.
 *
 * One wrapper around fetch, instead of an edit to each of the ~20 modules that
 * build their own auth headers, so no current or future module can forget it.
 * Deliberately narrow: only requests to a dialer origin, only while a tenant is
 * active, and never overriding a header the caller set itself. Everything else
 * (Firebase, Google token refresh, third parties) passes through untouched.
 * Idempotent; returns an uninstall function.
 */
export function installActiveTenantFetch(
  host: FetchHost = globalThis as unknown as FetchHost,
  origins: Set<string> = dialerOrigins(),
): () => void {
  const slot = host as unknown as Record<string, (() => void) | undefined>;
  const existing = slot[UNINSTALL_KEY];
  if (existing) return existing;

  const originalDescriptor = Object.getOwnPropertyDescriptor(host, 'fetch');
  const original = host.fetch.bind(host);
  const wrapped = ((input: RequestInfo | URL, init?: RequestInit) => {
    const tenant = active;
    if (!tenant) return original(input, init);

    let origin: string;
    try {
      origin = new URL(requestUrl(input), window.location.href).origin;
    } catch {
      return original(input, init);
    }
    if (!origins.has(origin)) return original(input, init);

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    if (!headers.has(ACT_AS_TENANT_HEADER)) headers.set(ACT_AS_TENANT_HEADER, tenant);
    if (input instanceof Request && !init) return original(new Request(input, { headers }));
    return original(input, { ...init, headers });
  }) as typeof fetch;
  setFetch(host, wrapped);

  const uninstall = () => {
    // Put back exactly what was there, read-only flag included.
    if (originalDescriptor) Object.defineProperty(host, 'fetch', originalDescriptor);
    else setFetch(host, original);
    delete slot[UNINSTALL_KEY];
  };
  slot[UNINSTALL_KEY] = uninstall;
  return uninstall;
}
